import { useCallback } from 'react'
import { Box, Stack } from '@mui/material'

import { useRecordingImages } from '@/apps/latest/features/attachments/useRecordingImages'
import { unreadAttachmentIds } from '@/apps/latest/features/attachments/unreadAttachments'
import { useHomeVisitReportEditing } from '@/apps/latest/features/home-visit/useHomeVisitReportEditing'
import { MetaText } from '@/components/atoms/MetaText'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { type YakurekiDetail } from '@/shared/types/yakurekiCore'
import { type SoapContentItem } from '@/shared/types/yakurekiSoap'

import { CopyableHeading } from './CopyableHeading'
import { SectionRenderer } from './SectionRenderer'
import {
  HOME_VISIT_REPORT_FIELDS,
  itemsOfField,
  selectedText,
} from './homeVisitFields'
import { useCopyText } from './hooks/useSoapCopy'

interface Props {
  event: YakurekiDetail
  // 渡すと SOAP の直下に置く形 (罫線 + 見出し「訪問報告書」、見出しで全体コピー) で描く。
  // β は報告書タブを持たず今の本番どおりここに出す (yakureki-back log/267)
  onCopyAll?: () => void
}

// 訪問報告書 (在宅のみ)。α は報告書タブの本文で、タブが文書種別を、カード右上の印刷・全体コピーが
// 出す操作を担うので見出しは置かない (トレーシングのタブと同じ。design-book ADR-0056)。
// β は SOAP の直下に見出しつきで置く (onCopyAll)。
// SOAP と同じ操作感 (行頭チェックボックスで採否 / 本文クリックで編集 / Enter で連続追加 /
// 見出しクリックでコピー) を、SoapBody と共通の SectionRenderer で描画する。
// 採否は is_selected フィールド (SoapBody と共通の起点だが、永続経路は home-visit-report
// PATCH で SoapBody の updateSoap とは別)。編集は home-visit-report PATCH で fields blob を保存し、
// 読み取りの正典は soaps サイドカー (編集後は HomeVisitReport.fields 優先)。フィールドモデルは
// homeVisitFields.ts の単一 SSoT。
export const HomeVisitReportPanel = ({ event, onCopyAll }: Props) => {
  const copyText = useCopyText()
  const editing = useHomeVisitReportEditing({ event })
  // 報告書を作ったあとに届いた資料。手を入れていない報告書は資料が SOAP に入るたびに作り直される
  // ので、ふつうは残らない。手を入れた報告書は作り直さないので、ここに件数が残る (log/261 決定 15)。
  // 作り直している間は数えない (古い報告書で数えると、これから入る資料が「入っていない」と出る)
  const { list: images } = useRecordingImages(event.id)
  const unread = event.soaps?.home_visit_generating
    ? null
    : unreadAttachmentIds(
        images.data ?? [],
        event.soaps?.home_visit_attachment_ids,
        event.soaps?.home_visit_skipped_attachment_ids ?? []
      )

  // 各フィールド見出しの単独コピー。提供先フォーマットへは項目単位で貼るのでこれが主操作。
  const copyField = useCallback(
    (label: string, items: SoapContentItem[]) => {
      void copyText(selectedText(items), `${label}をコピーしました`)
    },
    [copyText]
  )

  const body = (
    <Stack spacing={2}>
      {HOME_VISIT_REPORT_FIELDS.map(({ key, label }) => {
        const items = itemsOfField(event.soaps, key)
        return (
          <SectionRenderer
            key={key}
            sectionKey={key}
            label={label}
            items={items}
            editing={editing}
            strike={{
              // 採否 = is_selected フィールド (index で識別)。
              isStruck: (item) => !(item.is_selected ?? true),
              toggleLine: (_item, i) => editing.toggleSelect(key, i),
            }}
            onCopy={() => copyField(label, items)}
            copyTitle={`${label}をコピー`}
            medications={[]}
            onPlaceholderReplace={async () => false}
          />
        )
      })}
      {unread && unread.length > 0 && (
        <MetaText>
          報告書に入っていない資料が {unread.length} 件あります
        </MetaText>
      )}
    </Stack>
  )

  if (!onCopyAll) return body

  return (
    <Box
      sx={{
        mt: 2,
        pt: 1.5,
        borderTop: `1px solid ${colorTokens.border.default}`,
      }}
    >
      <CopyableHeading
        label="訪問報告書"
        title="訪問報告書をまとめてコピー"
        onCopy={onCopyAll}
      />
      <Box sx={{ mt: 1 }}>{body}</Box>
    </Box>
  )
}
